import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { AlertaService } from '../../../../../shared/utils/alerta.service';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import {
  AgendamentoResponse,
  ErroAgendamento,
  NovoAgendamento,
  ServicoAgendavelResponse,
} from '../../../models/agendamento.model';
import { DependenteResponse } from '../../../models/dependente.model';
import { AgendaMensalStore, DIAS_SEMANA, DiaCalendario } from '../../../services/agenda-mensal.store';
import {
  AgendamentosService,
  dataEHoraDe,
  idDoPosto,
  mapearErroAgendamento,
} from '../../../services/agendamentos.service';
import { MeusDependentesService } from '../../../services/meus-dependentes.service';

/** Uma opção do campo "Cidadão". O vínculo já vai dentro do `nome`, como o resumo o mostra. */
export interface CidadaoAgendamento {
  id: string;
  nome: string;
}

export interface ServicoAgendamento {
  id: string;
  nome: string;
}

/** Um posto do serviço escolhido, como a etapa Posto o mostra. */
interface PostoAgendamento {
  /** `'principal'` ou `'local-{código}'` — só identifica o posto DENTRO do serviço. */
  id: string;
  nome: string;
  endereco: string;
  /** Nulo é o endereço principal do serviço. */
  codLocal: number | null;
}

type PassoId = 'cidadao' | 'servico' | 'posto' | 'data-hora' | 'confirmacao';

interface PassoAgendamento {
  id: PassoId;
  titulo: string;
  icone: string;
}

/** O que a etapa "Cidadão" mostra abaixo do campo — ou, sem sessão, no lugar dele. */
type EstadoCidadaos = 'sessao' | 'carregando' | 'falha' | 'pronto';

type EstadoServicos = 'carregando' | 'falha' | 'vazio' | 'pronto';

/**
 * O que o bloco "Selecione um Horário" mostra: o convite a escolher a data, a grade de horários,
 * o aviso de data sem horário livre — ou nada, enquanto o mês carrega ou falhou (o calendário,
 * ao lado, já diz qual dos dois).
 */
type EstadoHorarios = 'sem-data' | 'lista' | 'esgotado' | 'aguardando';

const PASSOS: readonly PassoAgendamento[] = [
  { id: 'cidadao', titulo: 'Cidadão', icone: 'user' },
  { id: 'servico', titulo: 'Serviço', icone: 'clipboard-list' },
  { id: 'posto', titulo: 'Posto', icone: 'house' },
  { id: 'data-hora', titulo: 'Data e Hora', icone: 'clock' },
  { id: 'confirmacao', titulo: 'Confirmação', icone: 'shield-check' },
];

const ORIENTACOES: readonly string[] = [
  'Ao agendar o seu atendimento, você se compromete a desmarcar com antecedência no caso de impossibilidade no seu comparecimento;',
  'Não desmarcar resultará no bloqueio temporário para realização de novos agendamentos;',
  'Você pode alterar a data e o horário ou cancelar o agendamento em "Meus agendamentos", dentro do prazo informado na confirmação e no detalhe do agendamento. Depois dele, procure o órgão responsável;',
  'No dia e horário agendado para atendimento, favor comparecer à recepção do posto munido dos documentos exigidos para a realização do serviço desejado;',
  'Para atendimento presencial nas unidades da Rede SAC, recomenda-se comparecer com antecedência de 15 minutos para emissão da senha de atendimento, nas demais unidades, verificar no descritivo do serviço o prazo recomendado pelo órgão gestor;',
  'Para modalidade Videoatendimento será encaminhado um e-mail de acesso à sala virtual, onde será realizado o atendimento. Desta forma, reforçamos que fique atento ao Spam e ao Lixo Eletrônico para não perder o link.',
];

const DIRETRIZES_SERVICO: readonly string[] = [
  'Escolha o serviço que corresponde ao atendimento de que você precisa: cada agendamento vale para um único serviço;',
  'Para realizar mais de um serviço, conclua este agendamento e faça um novo para cada serviço adicional;',
  'Na lista aparecem apenas os serviços que oferecem atendimento por agendamento. Os demais estão disponíveis em "Todos os serviços";',
  'Antes de comparecer ao posto, consulte na página do serviço os documentos e requisitos exigidos.',
];

/**
 * Quem pode ser atendido: o titular (quem está logado) e, depois dele, os dependentes na ordem
 * em que a API os devolve.
 *
 * Sem nome de titular não há sessão, e a lista sai vazia mesmo que ainda haja dependentes na
 * memória. `dependentes` nulo é "carregando" ou "falhou": o titular já pode ser escolhido.
 *
 * O `id` leva o código do dependente, não o nome — homônimos continuam opções distintas. O
 * rótulo é sempre "(Dependente)", sem o parentesco: decisão da usuária (05/10/2026).
 */
export function cidadaosDoAgendamento(
  nomeTitular: string,
  dependentes: readonly DependenteResponse[] | null,
): CidadaoAgendamento[] {
  const titular = nomeTitular.trim();

  if (!titular) return [];

  return [
    { id: 'titular', nome: `${titular} (Titular)` },
    ...(dependentes ?? []).map((dependente) => ({
      id: `dependente-${dependente.codDependenteDpd}`,
      nome: `${dependente.desNomeDpd} (Dependente)`,
    })),
  ];
}

/**
 * As opções do campo "Serviço", na ordem em que a API devolve os serviços agendáveis (alfabética).
 *
 * Não há filtro nem ordenação aqui: quem decide o que dá para agendar — tipo `Agendamento`, sem
 * gov.br, com ao menos um posto — é o back-end. O `id` é o código, e homônimos continuam opções
 * distintas.
 */
export function servicosDoAgendamento(servicos: readonly ServicoAgendavelResponse[]): ServicoAgendamento[] {
  return servicos.map((servico) => ({ id: String(servico.codServicoSrv), nome: servico.desNomeSrv.trim() }));
}

/** Texto do cadastro (nome do cidadão, endereço do posto) dentro do HTML de um alerta. */
/**
 * A linha do prazo no alerta "Agendamento confirmado!", em HTML — até quando o cidadão pode
 * alterar ou cancelar pelo portal, com os limites e os `bit` que o `201` devolveu.
 *
 * Quem agenda em cima da hora já nasce fora do prazo: o alerta diz isso, em vez de prometer um
 * botão que "Meus agendamentos" não vai mostrar. O número de horas do prazo não mora no portal.
 */
function prazoDoAgendamento(agendamento: AgendamentoResponse): string {
  const ate = (iso: string) => `até <b>${escaparHtml(dataEHoraDe(iso))}</b>`;

  if (!agendamento.bitPodeReagendar && !agendamento.bitPodeCancelar) {
    return 'Pela proximidade do horário, ele não pode mais ser alterado nem cancelado pelo portal. Se precisar, procure o órgão responsável.';
  }

  if (
    agendamento.bitPodeReagendar &&
    agendamento.bitPodeCancelar &&
    agendamento.datLimiteReagendamento === agendamento.datLimiteCancelamento
  ) {
    return `Você pode alterá-lo ou cancelá-lo pelo portal ${ate(agendamento.datLimiteReagendamento)}.`;
  }

  const frases: string[] = [];

  if (agendamento.bitPodeReagendar) frases.push(`Você pode alterá-lo pelo portal ${ate(agendamento.datLimiteReagendamento)}.`);
  if (agendamento.bitPodeCancelar) frases.push(`Você pode cancelá-lo pelo portal ${ate(agendamento.datLimiteCancelamento)}.`);

  return frases.join(' ');
}

function escaparHtml(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

@Component({
  selector: 'app-agendamento',
  templateUrl: './agendamento.component.html',
  styleUrls: ['./agendamento.component.css'],
  imports: [LucideModule, RouterLink],
  // O calendário é deste wizard: outra instância do mesmo store serve o modal da página do serviço.
  providers: [AgendaMensalStore],
})
export class AgendamentoComponent {
  private readonly alerta = inject(AlertaService);
  private readonly auth = inject(AuthService);
  private readonly meusDependentes = inject(MeusDependentesService);
  private readonly agendamentos = inject(AgendamentosService);
  private readonly agenda = inject(AgendaMensalStore);
  private readonly destroyRef = inject(DestroyRef);

  private readonly campoCidadao = viewChild<ElementRef<HTMLSelectElement>>('campoCidadao');
  private readonly campoServico = viewChild<ElementRef<HTMLSelectElement>>('campoServico');

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  /**
   * Distingue "sessão encerrada" de "falha": num 401 o `ErrorInterceptor` faz logout, e a aba
   * continua na tela.
   */
  private readonly logado = computed(() => !!this.usuario()?.token);

  readonly passos = PASSOS;
  readonly diasSemana = DIAS_SEMANA;

  readonly passoAtual = signal(0);

  readonly orientacoes = ORIENTACOES;
  readonly diretrizesServico = DIRETRIZES_SERVICO;

  /**
   * O titular é quem está logado, com o nome da saudação do cabeçalho (o e-mail como reserva).
   * Sem sessão a lista é vazia: os dependentes de quem saiu não continuam na tela.
   */
  readonly cidadaos = computed(() => {
    const usuario = this.logado() ? this.usuario() : null;
    const titular = usuario?.nome?.trim() || usuario?.email?.trim() || '';

    return cidadaosDoAgendamento(titular, this.meusDependentes.dependentes());
  });

  /**
   * Presa à lista: a chegada dos dependentes não desfaz a escolha já feita, e a lista vazia do
   * fim de sessão a limpa.
   */
  readonly cidadaoSelecionado = linkedSignal<CidadaoAgendamento[], string>({
    source: this.cidadaos,
    computation: (cidadaos, anterior) => {
      const escolhido = anterior?.value ?? '';

      return cidadaos.some((cidadao) => cidadao.id === escolhido) ? escolhido : '';
    },
  });

  readonly servicos = computed(() => servicosDoAgendamento(this.agendamentos.servicos() ?? []));

  readonly servicoSelecionado = linkedSignal<ServicoAgendamento[], string>({
    source: this.servicos,
    computation: (servicos, anterior) => {
      const escolhido = anterior?.value ?? '';

      return servicos.some((servico) => servico.id === escolhido) ? escolhido : '';
    },
  });

  private readonly servicoEscolhido = computed<ServicoAgendavelResponse | null>(() => {
    const id = this.servicoSelecionado();

    return (this.agendamentos.servicos() ?? []).find((servico) => String(servico.codServicoSrv) === id) ?? null;
  });

  /** Os postos do serviço escolhido, na ordem da API: o endereço principal primeiro. */
  readonly postos = computed<readonly PostoAgendamento[]>(() =>
    (this.servicoEscolhido()?.postos ?? []).map((posto) => ({
      id: idDoPosto(posto),
      nome: posto.desNomePosto,
      endereco: posto.desEndereco,
      codLocal: posto.codLocalAtendimentoServicoLas,
    })),
  );

  /**
   * Presa ao serviço **e** à lista de postos dele.
   *
   * ⚠️ O serviço está na fonte de propósito: o id `'principal'` existe em TODO serviço. Presa só
   * à lista, a escolha do endereço principal de um serviço seria "válida" no seguinte — e
   * levaria junto a data e o horário de outra agenda. Serviço diferente zera a escolha; o mesmo
   * serviço (a lista só foi relida) mantém a que ainda existir.
   *
   * Serviço com um posto só já vem com ele escolhido: não há o que decidir.
   */
  readonly postoSelecionado = linkedSignal<{ servico: string; postos: readonly PostoAgendamento[] }, string>({
    source: () => ({ servico: this.servicoSelecionado(), postos: this.postos() }),
    computation: ({ servico, postos }, anterior) => {
      const escolhido = anterior?.source.servico === servico ? anterior.value : '';

      if (postos.some((posto) => posto.id === escolhido)) return escolhido;

      return postos.length === 1 ? postos[0].id : '';
    },
  });

  readonly postoEscolhido = computed<PostoAgendamento | null>(
    () => this.postos().find((posto) => posto.id === this.postoSelecionado()) ?? null,
  );

  /**
   * A agenda de onde a data e o horário saíram: o serviço **e** o posto. Só o posto não basta —
   * `'principal'` em dois serviços é o mesmo texto e são duas agendas.
   */
  private readonly chaveAgenda = computed(() => `${this.servicoSelecionado()}|${this.postoSelecionado()}`);

  /**
   * Presas à agenda: outro serviço ou outro posto zera a data e o horário, por qualquer caminho
   * — inclusive quando o serviço escolhido some da lista relida.
   */
  readonly dataSelecionada = linkedSignal<string, string>({ source: this.chaveAgenda, computation: () => '' });
  readonly horarioSelecionado = linkedSignal<string, string>({ source: this.chaveAgenda, computation: () => '' });

  readonly declaracaoAceita = signal(false);

  /** Um envio no ar: trava o segundo clique e marca o botão como ocupado. */
  readonly enviando = signal(false);

  /** O erro do último envio, mostrado na Confirmação. */
  readonly erroEnvio = signal<ErroAgendamento | null>(null);

  /** O aviso do horário tomado no envio (409), mostrado ao voltar à etapa Data e Hora. */
  readonly avisoHorario = signal('');

  readonly estadoCidadaos = computed<EstadoCidadaos>(() => {
    if (!this.logado()) return 'sessao';
    if (this.meusDependentes.falhou()) return 'falha';

    return this.meusDependentes.dependentes() === null ? 'carregando' : 'pronto';
  });

  readonly estadoServicos = computed<EstadoServicos>(() => {
    if (this.agendamentos.falhouServicos()) return 'falha';

    const servicos = this.agendamentos.servicos();

    if (servicos === null) return 'carregando';

    return servicos.length > 0 ? 'pronto' : 'vazio';
  });

  readonly passo = computed(() => this.passos[this.passoAtual()]);

  // O calendário da etapa Data e Hora é o do store; aqui ficam só os nomes que o template usa.
  readonly rotuloMes = this.agenda.rotuloMes;
  readonly diasDoMes = this.agenda.diasDoMes;
  readonly carregandoAgenda = this.agenda.carregando;
  readonly falhouAgenda = this.agenda.falhou;
  readonly mesSemHorarios = this.agenda.mesSemHorarios;
  readonly podeVoltarMes = this.agenda.podeVoltarMes;
  readonly podeAvancarMes = this.agenda.podeAvancarMes;

  readonly horariosDoDia = computed<readonly string[]>(() => this.agenda.horariosDe(this.dataSelecionada()));

  readonly estadoHorarios = computed<EstadoHorarios>(() => {
    const data = this.dataSelecionada();
    const { ano, mes } = this.agenda.mesReferencia();

    // Data de outro mês: o calendário exibido não tem os horários dela.
    if (!data || data.slice(0, 7) !== `${ano}-${String(mes + 1).padStart(2, '0')}`) return 'sem-data';

    if (this.horariosDoDia().length > 0) return 'lista';

    return this.agenda.carregando() || this.agenda.falhou() ? 'aguardando' : 'esgotado';
  });

  readonly nomeCidadao = computed(
    () => this.cidadaos().find((c) => c.id === this.cidadaoSelecionado())?.nome ?? ''
  );

  readonly nomeServico = computed(
    () => this.servicos().find((s) => s.id === this.servicoSelecionado())?.nome ?? ''
  );

  readonly nomePosto = computed(() => this.postoEscolhido()?.nome ?? '');

  readonly dataFormatada = computed(() => {
    const iso = this.dataSelecionada();
    if (!iso) return '';

    const [ano, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${ano}`;
  });

  readonly resumoDataHora = computed(() =>
    this.dataSelecionada() && this.horarioSelecionado()
      ? `${this.dataFormatada()} às ${this.horarioSelecionado()}`
      : ''
  );

  readonly podeAvancar = computed(() => {
    switch (this.passo().id) {
      case 'cidadao':
        return !!this.cidadaoSelecionado();
      case 'servico':
        return !!this.servicoSelecionado();
      case 'posto':
        return !!this.postoSelecionado();
      case 'data-hora':
        return !!this.dataSelecionada() && !!this.horarioSelecionado();
      default:
        return true;
    }
  });

  /**
   * O que será enviado, ou `null` enquanto faltar alguma escolha — o que só acontece na
   * Confirmação quando o serviço some da lista relida depois de um 404.
   */
  private readonly novoAgendamento = computed<NovoAgendamento | null>(() => {
    const servico = this.servicoEscolhido();
    const posto = this.postoEscolhido();
    const idCidadao = this.cidadaoSelecionado();
    const data = this.dataSelecionada();
    const horario = this.horarioSelecionado();

    if (!servico || !posto || !idCidadao || !data || !horario) return null;

    return { codServicoSrv: servico.codServicoSrv, idPosto: posto.id, data, horario, idCidadao };
  });

  readonly podeConfirmar = computed(
    () => this.declaracaoAceita() && !this.enviando() && this.novoAgendamento() !== null,
  );

  constructor() {
    this.agendamentos.carregarServicos();

    // Sessão encerrada no meio do fluxo (um 401): a escolha do cidadão some com a lista, mas a
    // etapa não — sem voltar ao início, a Confirmação mostraria um resumo sem cidadão.
    effect(() => {
      if (!this.logado()) this.reiniciar();
    });

    // O calendário segue o serviço e o posto escolhidos: outra agenda, outra carga. Sem posto
    // (inclusive depois do `reiniciar()`), a fonte é esquecida — o mesmo posto escolhido de
    // novo relê a agenda, em vez de mostrar a fotografia de antes do agendamento.
    effect(() => {
      const servico = this.servicoEscolhido();
      const posto = this.postoEscolhido();

      untracked(() => {
        const mudou =
          servico && posto
            ? this.agenda.definirFonte(servico.codServicoSrv, posto.codLocal)
            : this.agenda.definirFonte(null);

        if (mudou) this.agenda.carregarMes();
      });
    });
  }

  situacaoDoPasso(indice: number): 'concluido' | 'andamento' | 'aguardando' {
    if (indice < this.passoAtual()) return 'concluido';
    if (indice === this.passoAtual()) return 'andamento';
    return 'aguardando';
  }

  rotuloSituacao(indice: number): string {
    switch (this.situacaoDoPasso(indice)) {
      case 'concluido':
        return 'Concluído';
      case 'andamento':
        return 'Em andamento';
      default:
        return indice === this.passos.length - 1 ? 'Passo final' : 'Aguardando';
    }
  }

  avancar(): void {
    if (!this.podeAvancar()) return;

    this.passoAtual.update((passo) => Math.min(passo + 1, this.passos.length - 1));
  }

  voltar(): void {
    // O erro de envio é da tentativa que ficou para trás.
    this.erroEnvio.set(null);
    this.passoAtual.update((passo) => Math.max(passo - 1, 0));
  }

  aoSelecionarCidadao(evento: Event): void {
    this.cidadaoSelecionado.set((evento.target as HTMLSelectElement).value);
  }

  /**
   * Refaz a busca dos dependentes. O foco vai para o campo: o botão some quando a busca dá
   * certo, e o foco não pode sumir junto.
   */
  tentarNovamente(): void {
    this.meusDependentes.recarregar();
    this.campoCidadao()?.nativeElement.focus();
  }

  /** Outro serviço é outra lista de postos e outra agenda: o posto, a data e o horário caem. */
  aoSelecionarServico(evento: Event): void {
    this.servicoSelecionado.set((evento.target as HTMLSelectElement).value);
    this.limparDataEHora();
  }

  tentarServicosNovamente(): void {
    this.agendamentos.recarregarServicos();
    this.campoServico()?.nativeElement.focus();
  }

  /** Outro posto é outra agenda: a data e o horário caem. */
  aoSelecionarPosto(evento: Event): void {
    this.postoSelecionado.set((evento.target as HTMLSelectElement).value);
    this.limparDataEHora();
  }

  selecionarDia(dia: DiaCalendario): void {
    if (dia.dia === null || dia.indisponivel) return;

    this.dataSelecionada.set(dia.iso);
    this.horarioSelecionado.set('');
    this.avisoHorario.set('');
  }

  selecionarHorario(horario: string): void {
    this.horarioSelecionado.set(horario);
    this.avisoHorario.set('');
  }

  alternarDeclaracao(evento: Event): void {
    this.declaracaoAceita.set((evento.target as HTMLInputElement).checked);
  }

  mesAnterior(): void {
    this.agenda.mesAnterior();
  }

  proximoMes(): void {
    this.agenda.proximoMes();
  }

  /** "Tentar novamente" da falha do calendário. */
  recarregarAgenda(): void {
    this.agenda.recarregar();
  }

  /**
   * Envia o agendamento. A vaga só é garantida aqui: o que o calendário mostrou era uma
   * fotografia, e o horário pode ter sido tomado enquanto o cidadão revisava.
   */
  confirmar(): void {
    // O `[disabled]` só chega ao botão na próxima renderização: o segundo clique de um duplo
    // clique ainda entra aqui.
    if (!this.podeConfirmar()) return;

    const novo = this.novoAgendamento();

    if (novo === null) return;

    // Lidos antes do envio: o `reiniciar()` do sucesso apaga as escolhas.
    const resumo = { cidadao: this.nomeCidadao(), posto: this.nomePosto(), quando: this.resumoDataHora() };

    this.enviando.set(true);
    this.erroEnvio.set(null);

    this.agendamentos
      .agendar(novo)
      .pipe(
        finalize(() => this.enviando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (agendamento) => void this.aoAgendar(agendamento, resumo),
        error: (erro: unknown) => this.aoFalharEnvio(mapearErroAgendamento(erro, 'agendar')),
      });
  }

  /** Não há `<dialog>` aberto neste fluxo: o alerta pode abrir direto. */
  private async aoAgendar(
    agendamento: AgendamentoResponse,
    resumo: { cidadao: string; posto: string; quando: string },
  ): Promise<void> {
    await this.alerta.sucesso({
      titulo: 'Agendamento confirmado!',
      html:
        `Atendimento de <b>${escaparHtml(resumo.cidadao)}</b> em <b>${escaparHtml(resumo.posto)}</b><br>` +
        `${escaparHtml(resumo.quando)}.<br>` +
        `Protocolo: <b>${escaparHtml(agendamento.desProtocoloSsv)}</b><br>` +
        'Ele fica disponível em "Meus agendamentos".<br>' +
        prazoDoAgendamento(agendamento),
      confirmar: 'Concluir',
    });

    this.reiniciar();
  }

  private aoFalharEnvio(erro: ErroAgendamento): void {
    switch (erro.tipo) {
      case 'vaga': {
        // O horário foi tomado: a escolha volta para a etapa Data e Hora, com o mês da data
        // relido — o horário perdido (ou o dia inteiro) já não aparece.
        const data = this.dataSelecionada();

        this.horarioSelecionado.set('');
        this.avisoHorario.set(erro.mensagem);
        this.irPara('data-hora');

        if (data) {
          this.agenda.irParaMesDe(data);
        } else {
          this.agenda.recarregar();
        }

        return;
      }

      case 'nao-encontrado':
        // O serviço saiu do ar: a lista relida já não o oferece.
        this.erroEnvio.set(erro);
        this.agendamentos.recarregarServicos();
        return;

      default:
        // No 401 o `ErrorInterceptor` encerra a sessão, e o `effect` do construtor reinicia o
        // wizard: a etapa Cidadão mostra "Sua sessão foi encerrada.". Nos demais o aviso fica
        // na Confirmação, e as escolhas ficam.
        this.erroEnvio.set(erro);
    }
  }

  private irPara(id: PassoId): void {
    this.passoAtual.set(this.passos.findIndex((passo) => passo.id === id));
  }

  /**
   * A data e o horário são de uma agenda — a do serviço e do posto que acabaram de ser trocados.
   * As duas já são presas à agenda (`chaveAgenda`); zerar aqui garante a limpeza no momento da
   * troca, mesmo que o cidadão volte ao mesmo posto antes de alguém as ler.
   */
  private limparDataEHora(): void {
    this.dataSelecionada.set('');
    this.horarioSelecionado.set('');
    this.avisoHorario.set('');
  }

  private reiniciar(): void {
    this.passoAtual.set(0);
    this.cidadaoSelecionado.set('');
    this.servicoSelecionado.set('');
    this.postoSelecionado.set('');
    this.dataSelecionada.set('');
    this.horarioSelecionado.set('');
    this.declaracaoAceita.set(false);
    this.erroEnvio.set(null);
    this.avisoHorario.set('');
  }
}
